if (!customElements.get('free-shipping-bar')) {
  customElements.define(
    'free-shipping-bar',
    class FreeShippingBar extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) {
          this.initialized = true;
          this.messageElement = this.querySelector('[data-free-shipping-message]');
          this.trackElement = this.querySelector('[data-free-shipping-track]');
          this.fillElement = this.querySelector('[data-free-shipping-fill]');
          this.thresholdCents = Number(this.dataset.thresholdCents) || 4990;
          this.locale = this.dataset.locale || 'it-IT';
          this.currency = this.dataset.currency || 'EUR';
          this.emptyMessage = this.dataset.emptyMessage || '';
          this.successMessage =
            this.dataset.successMessage || 'Soglia gratuita raggiunta per la zona standard';

          this.render({
            item_count: Number(this.dataset.cartItemCount) || 0,
            total_price: Number(this.dataset.cartTotalCents) || 0,
          });
        }

        this.subscribeToCartUpdates();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
      }

      subscribeToCartUpdates() {
        if (
          this.cartUpdateUnsubscriber ||
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          const itemCount = Number(cartData?.item_count);
          const totalPrice = Number(cartData?.total_price);

          if (Number.isFinite(itemCount) && Number.isFinite(totalPrice)) {
            this.render({ item_count: itemCount, total_price: totalPrice });
            return Promise.resolve();
          }

          return this.refreshFromCart();
        });
      }

      refreshFromCart() {
        this.refreshRequested = true;

        if (this.refreshPromise) return this.refreshPromise;

        this.refreshPromise = (async () => {
          do {
            this.refreshRequested = false;

            try {
              const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
              const response = await fetch(cartUrl, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
              });

              if (!response.ok) continue;

              const cart = await response.json();
              this.render(cart);
            } catch (error) {
              // Keep the last valid state if the cart endpoint is temporarily unavailable.
            }
          } while (this.refreshRequested);
        })().finally(() => {
          this.refreshPromise = undefined;
        });

        return this.refreshPromise;
      }

      render(cart) {
        if (!this.messageElement || !this.trackElement || !this.fillElement) return;

        const itemCount = Math.max(0, Number(cart?.item_count) || 0);
        const totalCents = Math.max(0, Number(cart?.total_price) || 0);
        const remainingCents = Math.max(0, this.thresholdCents - totalCents);
        const progress = this.thresholdCents > 0
          ? Math.min(100, (totalCents / this.thresholdCents) * 100)
          : 100;

        let message = this.emptyMessage;

        if (itemCount > 0 && totalCents >= this.thresholdCents) {
          message = this.successMessage;
        } else if (itemCount > 0) {
          message = `Ti mancano ${this.formatMoney(
            remainingCents
          )} alla soglia gratuita della zona standard · zone speciali: 69,90 €`;
        }

        this.messageElement.textContent = message;
        this.trackElement.hidden = itemCount === 0;
        this.trackElement.setAttribute('aria-valuenow', String(Math.round(progress)));
        this.trackElement.setAttribute('aria-valuetext', message);
        this.fillElement.style.setProperty('--free-shipping-progress', `${progress}%`);
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
        }
      }
    }
  );
}

class MorettiSuggestionEngine {
  constructor(config = {}) {
    this.config = config;
  }

  normalizeToken(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase('it-IT')
      .replace(/\s+/g, '');
  }

  normalizeItems(items) {
    if (!Array.isArray(items)) return [];

    return items
      .map((item) => ({
        handle: String(item?.handle || item?.product_handle || ''),
        productId: Number(item?.productId || item?.product_id) || 0,
        variantId: Number(item?.variantId || item?.variant_id || item?.id) || 0,
        variantTitle: String(item?.variantTitle || item?.variant_title || ''),
        quantity: Math.max(0, Number(item?.quantity) || 0),
      }))
      .filter((item) => item.quantity > 0 && (item.handle || item.productId || item.variantId));
  }

  getProductConfig(handle) {
    return this.config?.products?.[handle] || null;
  }

  getFunctions(items) {
    return [
      ...new Set(
        items
          .map((item) => this.getProductConfig(item.handle)?.function)
          .filter(Boolean)
      ),
    ];
  }

  getFunctionCompatibility(candidateFunction, sourceFunctions) {
    const scores = sourceFunctions
      .map((sourceFunction) =>
        Number(this.config?.functionAffinity?.[sourceFunction]?.[candidateFunction]) || 0
      )
      .filter((score) => score > 0)
      .sort((a, b) => b - a);

    if (!scores.length) return 0;
    return Math.min(100, scores[0] + scores.slice(1).reduce((sum, score) => sum + score * 0.12, 0));
  }

  getProductAffinity(candidateHandle, sourceItems) {
    const scores = sourceItems
      .flatMap((item) => {
        const forward = Number(this.config?.productAffinity?.[item.handle]?.[candidateHandle]) || 0;
        const reverse = Number(this.config?.productAffinity?.[candidateHandle]?.[item.handle]) || 0;
        return [forward, reverse * 0.9];
      })
      .filter((score) => score > 0)
      .sort((a, b) => b - a);

    if (!scores.length) return 0;
    return Math.min(100, scores[0] + scores.slice(1).reduce((sum, score) => sum + score * 0.1, 0));
  }

  getFlavorCompatibility(candidateFlavors, sourceItems) {
    if (!Array.isArray(candidateFlavors) || !candidateFlavors.length) return 0;

    const sourceFlavors = [
      ...new Set(
        sourceItems.flatMap((item) => this.getProductConfig(item.handle)?.flavors || [])
      ),
    ];
    if (!sourceFlavors.length) return 0;
    if (candidateFlavors.includes('universal')) return 78;

    const exactMatches = candidateFlavors.filter((flavor) => sourceFlavors.includes(flavor));
    if (!exactMatches.length) {
      const bothGastronomic =
        candidateFlavors.some((flavor) => ['truffle', 'porcini', 'mushroom'].includes(flavor)) &&
        sourceFlavors.some((flavor) => ['truffle', 'porcini', 'mushroom'].includes(flavor));
      return bothGastronomic ? 42 : 20;
    }

    const strongest = Math.max(
      ...exactMatches.map((flavor) => {
        if (flavor === 'porcini') return 96;
        if (flavor === 'truffle' || flavor === 'white-truffle') return 90;
        if (flavor === 'mushroom') return 72;
        return 82;
      })
    );
    return Math.min(100, strongest + Math.max(0, exactMatches.length - 1) * 5);
  }

  getBundleMatchCount(bundleRule, cartItems) {
    if (!bundleRule?.members?.length) return 0;

    return bundleRule.members.reduce((count, member) => {
      const memberToken = this.normalizeToken(member.variantToken);
      const matched = cartItems.some((item) => {
        if (item.handle !== member.handle) return false;
        if (!memberToken) return true;
        return this.normalizeToken(item.variantTitle).includes(memberToken);
      });
      return count + Number(matched);
    }, 0);
  }

  getPriceTier(priceCents) {
    if (priceCents <= 790) return 'small';
    if (priceCents <= 1290) return 'medium';
    return 'large';
  }

  getGapFit(priceCents, remainingCents) {
    if (remainingCents <= 0 || priceCents <= 0) return 0;
    if (priceCents >= remainingCents) {
      const overshoot = priceCents - remainingCents;
      return Math.max(0, 100 - (overshoot / Math.max(remainingCents, 500)) * 75);
    }
    return Math.max(0, 50 * (priceCents / remainingCents));
  }

  getPriceEase(priceCents) {
    if (priceCents <= 690) return 100;
    if (priceCents <= 790) return 94;
    if (priceCents <= 890) return 86;
    if (priceCents <= 1090) return 76;
    if (priceCents <= 1290) return 64;
    if (priceCents <= 1590) return 48;
    if (priceCents <= 1990) return 32;
    return 14;
  }

  getFunctionalRepetitionPenalty(candidateFunction, sourceFunctions, cartFunctions) {
    const cartPenalty = {
      base: 64,
      ingredient: 48,
      extra: 40,
      'main-sauce': 34,
      finishing: 26,
      bundle: 64,
    };
    const sourcePenalty = {
      base: 52,
      ingredient: 38,
      extra: 32,
      'main-sauce': 26,
      finishing: 20,
      bundle: 52,
    };

    if (cartFunctions.includes(candidateFunction)) return cartPenalty[candidateFunction] || 30;
    if (sourceFunctions.includes(candidateFunction)) return sourcePenalty[candidateFunction] || 22;
    return 0;
  }

  buildCandidate(element, options = {}) {
    const cartItems = this.normalizeItems(options.cartItems || []);
    const sourceItems = this.normalizeItems(options.sourceItems || cartItems);
    const totalCents = Math.max(0, Number(options.totalCents) || 0);
    const remainingCents = Math.max(0, Number(options.remainingCents) || 0);
    const handle = element.dataset.productHandle || '';
    const productId = Number(element.dataset.productId) || 0;
    const variantId = Number(element.dataset.variantId) || 0;
    const variantTitle = element.dataset.variantTitle || '';
    const priceCents = Number(element.dataset.priceCents) || 0;
    const compareAtPriceCents = Number(element.dataset.compareAtPriceCents) || 0;
    const available = element.dataset.available === 'true';
    const productConfig = this.getProductConfig(handle);
    const bundleRule = this.config?.bundles?.[handle];
    const productAlreadyOwned = [...sourceItems, ...cartItems].some(
      (item) => item.handle === handle || (productId && item.productId === productId)
    );
    const exactVariantInCart = cartItems.some((item) => item.variantId === variantId);

    if (
      !productConfig ||
      !available ||
      !variantId ||
      priceCents <= 0 ||
      exactVariantInCart ||
      productAlreadyOwned
    ) {
      return null;
    }

    const sourceFunctions = this.getFunctions(sourceItems);
    const cartFunctions = this.getFunctions(cartItems);
    const candidateFunction = productConfig.function || '';
    const functionScore = this.getFunctionCompatibility(candidateFunction, sourceFunctions);
    const productAffinity = this.getProductAffinity(handle, sourceItems);
    const flavorScore = this.getFlavorCompatibility(productConfig.flavors || [], sourceItems);
    const bundleCartMatchCount = this.getBundleMatchCount(bundleRule, cartItems);
    const bundleSourceMatchCount = this.getBundleMatchCount(bundleRule, sourceItems);
    const bundleMatchCount = Math.max(bundleCartMatchCount, bundleSourceMatchCount);
    const isBundleOpportunity = Boolean(bundleRule && bundleMatchCount > 0);
    if (candidateFunction === 'bundle' && !isBundleOpportunity) return null;
    const functionalPenalty = this.getFunctionalRepetitionPenalty(
      candidateFunction,
      sourceFunctions,
      cartFunctions
    );
    let compatibilityScore = Math.min(
      100,
      functionScore * 0.62 + flavorScore * 0.24 + productAffinity * 0.3
    );

    if (isBundleOpportunity) {
      compatibilityScore = Math.max(
        compatibilityScore,
        Math.min(100, (Number(bundleRule.baseScore) || 82) + (bundleMatchCount - 1) * 7)
      );
    }

    const premiumPenalty = productConfig.premium && flavorScore < 72 ? 32 : 0;
    compatibilityScore = Math.max(0, compatibilityScore - functionalPenalty - premiumPenalty);
    const isPromo = compareAtPriceCents > priceCents;
    const relevant = compatibilityScore >= 40 || isBundleOpportunity;
    if (!relevant) return null;

    const regime = totalCents < 3000 ? 'low' : remainingCents <= 1500 ? 'near' : 'mid';
    const gapFit = this.getGapFit(priceCents, remainingCents);
    const priceEase = this.getPriceEase(priceCents);
    const priority = Math.max(0, Number(productConfig.priority) || 0);
    const preferredToken = this.normalizeToken(productConfig.preferredVariantToken);
    const preferredVariant = Boolean(
      preferredToken && this.normalizeToken(variantTitle).includes(preferredToken)
    );
    const reviewUpgrade = Boolean(bundleRule?.reviewOnMemberMatch && bundleCartMatchCount > 0);
    const directScore = reviewUpgrade ? 0 : 6;
    const promoScore = isPromo ? 5 : 0;
    let rankingScore;

    if (regime === 'near') {
      rankingScore =
        compatibilityScore * 0.72 +
        gapFit * 1.1 +
        directScore +
        priority * 2 +
        promoScore +
        Number(preferredVariant) * 2;
    } else if (regime === 'mid') {
      rankingScore =
        compatibilityScore * 0.94 +
        gapFit * 0.32 +
        priceEase * 0.08 +
        directScore +
        priority * 2 +
        promoScore +
        Number(preferredVariant) * 6;
    } else {
      rankingScore =
        compatibilityScore +
        priceEase * 0.16 +
        directScore +
        priority * 2 +
        promoScore +
        Number(preferredVariant) * 8;
    }

    return {
      element,
      handle,
      productId,
      variantId,
      variantTitle,
      priceCents,
      compareAtPriceCents,
      function: candidateFunction,
      priceTier: this.getPriceTier(priceCents),
      compatibilityScore,
      functionScore,
      flavorScore,
      productAffinity,
      functionalPenalty,
      gapFit,
      rankingScore,
      opportunityScore:
        compatibilityScore * 0.68 +
        Number(isPromo) * 26 +
        Number(isBundleOpportunity) * 30 +
        bundleMatchCount * 7 +
        priority * 2,
      isPromo,
      isBundleOpportunity,
      bundleMatchCount,
      bundleCartMatchCount,
      reviewUpgrade,
      closesThreshold: remainingCents > 0 && priceCents >= remainingCents,
      overshootCents:
        remainingCents > 0 && priceCents >= remainingCents
          ? priceCents - remainingCents
          : Number.POSITIVE_INFINITY,
      regime,
    };
  }

  chooseBestByProduct(candidates) {
    const bestByProduct = new Map();
    [...candidates]
      .sort((a, b) => b.rankingScore - a.rankingScore)
      .forEach((candidate) => {
        if (!bestByProduct.has(candidate.handle)) bestByProduct.set(candidate.handle, candidate);
      });
    return [...bestByProduct.values()];
  }

  getSelectionScore(candidate, chosen, regime) {
    const usedFunctions = new Set(chosen.map((item) => item.function));
    const usedTiers = new Set(chosen.map((item) => item.priceTier));
    const newFunctionBonus = usedFunctions.has(candidate.function)
      ? -38
      : regime === 'near'
        ? 20
        : 34;
    const newTierBonus = usedTiers.has(candidate.priceTier) ? 0 : regime === 'low' ? 9 : 4;
    return candidate.rankingScore + newFunctionBonus + newTierBonus;
  }

  canUseOpportunity(candidate, chosen) {
    if (!candidate || (!candidate.isPromo && !candidate.isBundleOpportunity)) return false;
    if (candidate.compatibilityScore < 45) return false;
    // A discount is a useful signal only when the product still adds a different
    // gastronomic role. It must not resurrect a sauce/cream already represented
    // in the cart simply because that variant is currently on promotion.
    if (candidate.isPromo && !candidate.isBundleOpportunity && candidate.functionalPenalty >= 30) {
      return false;
    }
    if (!chosen.length) return true;

    const candidateDiversity = chosen.some((item) => item.function === candidate.function) ? 0 : 12;
    const candidateScore = candidate.rankingScore + candidateDiversity;
    const wouldImprove = chosen.some((current, index) => {
      const otherFunctions = new Set(
        chosen.filter((_, chosenIndex) => chosenIndex !== index).map((item) => item.function)
      );
      const currentDiversity = otherFunctions.has(current.function) ? 0 : 12;
      return candidateScore >= current.rankingScore + currentDiversity - 10;
    });
    return wouldImprove;
  }

  enforceDiversity(chosen, pool, regime) {
    if (chosen.length < 2) return chosen;

    const result = [...chosen];
    const counts = () =>
      result.reduce((map, candidate) => {
        map.set(candidate.function, (map.get(candidate.function) || 0) + 1);
        return map;
      }, new Map());

    let functionCounts = counts();
    const duplicateIndexes = result
      .map((candidate, index) => ({ candidate, index }))
      .filter(({ candidate }) => (functionCounts.get(candidate.function) || 0) > 1)
      .sort((a, b) => a.candidate.rankingScore - b.candidate.rankingScore);

    duplicateIndexes.forEach(({ candidate, index }) => {
      functionCounts = counts();
      if ((functionCounts.get(candidate.function) || 0) <= 1) return;
      const usedHandles = new Set(result.map((item) => item.handle));
      const usedFunctions = new Set(result.map((item) => item.function));
      const replacement = pool
        .filter(
          (option) =>
            !usedHandles.has(option.handle) &&
            !usedFunctions.has(option.function) &&
            option.compatibilityScore >= 40
        )
        .sort(
          (a, b) =>
            this.getSelectionScore(b, result, regime) - this.getSelectionScore(a, result, regime)
        )[0];
      if (replacement) result[index] = replacement;
    });

    return result;
  }

  selectSuggestions(candidates, regime) {
    if (!candidates.length) return [];

    const pool = this.chooseBestByProduct(candidates);
    const chosen = [];
    const take = (candidate) => {
      if (!candidate || chosen.some((item) => item.handle === candidate.handle)) return;
      chosen.push(candidate);
    };
    const remaining = () => pool.filter((candidate) => !chosen.includes(candidate));
    const bestRemaining = () =>
      remaining().sort(
        (a, b) => this.getSelectionScore(b, chosen, regime) - this.getSelectionScore(a, chosen, regime)
      )[0];

    if (regime === 'near') {
      const preciseCloser = pool
        .filter((candidate) => candidate.closesThreshold && candidate.compatibilityScore >= 45)
        .sort(
          (a, b) =>
            a.overshootCents - b.overshootCents ||
            b.compatibilityScore - a.compatibilityScore ||
            b.rankingScore - a.rankingScore
        )[0];
      take(preciseCloser || [...pool].sort((a, b) => b.rankingScore - a.rankingScore)[0]);
    } else {
      take([...pool].sort((a, b) => b.rankingScore - a.rankingScore)[0]);
    }

    while (chosen.length < 3) {
      const next = bestRemaining();
      if (!next) break;
      take(next);
    }

    const opportunity = pool
      .filter(
        (candidate) =>
          !chosen.some((item) => item.handle === candidate.handle) &&
          this.canUseOpportunity(candidate, chosen)
      )
      .sort((a, b) => {
        const aDiversity = chosen.some((item) => item.function === a.function) ? 0 : 24;
        const bDiversity = chosen.some((item) => item.function === b.function) ? 0 : 24;
        return b.opportunityScore + bDiversity - (a.opportunityScore + aDiversity);
      })[0];
    if (opportunity && chosen.length === 3) {
      const replaceable = [...chosen]
        .map((candidate, index) => {
          const otherFunctions = new Set(
            chosen.filter((_, chosenIndex) => chosenIndex !== index).map((item) => item.function)
          );
          const diversityValue = otherFunctions.has(candidate.function) ? 0 : 12;
          return { candidate, index, value: candidate.rankingScore + diversityValue };
        })
        .filter(({ candidate }) => {
          if (candidate.isBundleOpportunity && candidate.bundleMatchCount >= 2) return false;
          if (
            (candidate.isPromo || candidate.isBundleOpportunity) &&
            candidate.opportunityScore >= opportunity.opportunityScore
          ) {
            return false;
          }
          return true;
        })
        .sort((a, b) => a.value - b.value)[0];
      const opportunityDiversity = chosen.some((item) => item.function === opportunity.function) ? 0 : 12;
      if (
        replaceable &&
        opportunity.rankingScore + opportunityDiversity >= replaceable.value - 10
      ) {
        chosen[replaceable.index] = opportunity;
      }
    } else {
      take(opportunity);
    }

    return this.enforceDiversity(chosen.slice(0, 3), pool, regime).slice(0, 3);
  }
}

if (!customElements.get('moretti-cart-goal')) {
  customElements.define(
    'moretti-cart-goal',
    class MorettiCartGoal extends HTMLElement {
      connectedCallback() {
        this.messageElement = this.querySelector('[data-moretti-goal-message]');
        this.trackElement = this.querySelector('[data-moretti-goal-track]');
        this.fillElement = this.querySelector('[data-moretti-goal-fill]');
        this.suggestionsElement = this.querySelector('[data-moretti-suggestions]');
        this.suggestionList = this.querySelector('[data-moretti-suggestion-list]');
        this.precheckoutSuccessElement = this.querySelector('[data-moretti-precheckout-success]');
        this.candidateElements = Array.from(this.querySelectorAll('[data-moretti-candidate]'));
        this.config = this.readEmbeddedJson('[data-moretti-merchandising-config]', {
          products: {},
          functionAffinity: {},
          productAffinity: {},
          bundles: {},
        });
        this.engine = new MorettiSuggestionEngine(this.config);
        this.initialCartItems = this.engine.normalizeItems(
          this.readEmbeddedJson('[data-moretti-precheckout-cart]', [])
        );
        this.thresholdCents = Number(this.dataset.thresholdCents) || 4990;
        this.locale = this.dataset.locale || 'it-IT';
        this.currency = this.dataset.currency || 'EUR';
        this.context = this.dataset.context || 'cart';

        this.render({
          item_count: Number(this.dataset.cartItemCount) || 0,
          total_price: Number(this.dataset.cartTotalCents) || 0,
          items: this.initialCartItems,
        });
        this.subscribeToCartUpdates();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
      }

      subscribeToCartUpdates() {
        if (
          this.cartUpdateUnsubscriber ||
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          const itemCount = Number(cartData?.item_count);
          const totalPrice = Number(cartData?.total_price);

          if (
            Number.isFinite(itemCount) &&
            Number.isFinite(totalPrice) &&
            Array.isArray(cartData?.items)
          ) {
            this.render({ item_count: itemCount, total_price: totalPrice, items: cartData.items });
            return Promise.resolve();
          }

          return this.refreshFromCart();
        });
      }

      refreshFromCart() {
        if (this.refreshPromise) return this.refreshPromise;

        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        this.refreshPromise = fetch(cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Cart unavailable');
            return response.json();
          })
          .then((cart) => this.render(cart))
          .catch(() => undefined)
          .finally(() => {
            this.refreshPromise = undefined;
          });

        return this.refreshPromise;
      }

      render(cart) {
        const itemCount = Math.max(0, Number(cart?.item_count) || 0);
        const totalCents = Math.max(0, Number(cart?.total_price) || 0);
        const remainingCents = Math.max(0, this.thresholdCents - totalCents);
        const cartItems = Array.isArray(cart?.items)
          ? this.engine.normalizeItems(cart.items)
          : this.initialCartItems;
        const progress =
          this.thresholdCents > 0 ? Math.min(100, (totalCents / this.thresholdCents) * 100) : 100;

        if (this.messageElement && this.trackElement && this.fillElement) {
          let message = 'Zona standard: 4,90 € · gratuita da 49,90 € · Nessun minimo d’ordine';

          if (itemCount > 0 && totalCents >= this.thresholdCents) {
            message = 'Soglia gratuita raggiunta per la zona standard';
          } else if (itemCount > 0) {
            message = `Ti mancano ${this.formatMoney(remainingCents)} alla soglia gratuita della zona standard`;
          }

          this.messageElement.textContent = message;
          this.trackElement.setAttribute('aria-valuenow', String(Math.round(progress)));
          this.trackElement.setAttribute('aria-valuetext', message);
          this.fillElement.style.setProperty('--moretti-cart-progress', `${progress}%`);
        }

        this.updateSuggestions(itemCount, totalCents, remainingCents, cartItems);
      }

      readEmbeddedJson(selector, fallback) {
        const source = this.querySelector(selector);
        if (!source?.textContent) return fallback;

        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          return fallback;
        }
      }

      normalizeToken(value) {
        return String(value || '')
          .trim()
          .toLocaleLowerCase('it-IT')
          .replace(/\s+/g, '');
      }

      normalizeCartItems(items) {
        if (!Array.isArray(items)) return [];

        return items
          .map((item) => ({
            handle: String(item?.handle || item?.product_handle || ''),
            productId: Number(item?.productId || item?.product_id) || 0,
            variantId: Number(item?.variantId || item?.variant_id || item?.id) || 0,
            variantTitle: String(item?.variantTitle || item?.variant_title || ''),
            quantity: Math.max(0, Number(item?.quantity) || 0),
          }))
          .filter((item) => item.quantity > 0 && (item.handle || item.productId || item.variantId));
      }

      getCartFamilies(cartItems) {
        return [
          ...new Set(
            cartItems
              .map((item) => this.config?.products?.[item.handle]?.family)
              .filter(Boolean)
          ),
        ];
      }

      getAffinityScore(candidateHandle, cartFamilies) {
        const scores = cartFamilies
          .map((family) => Number(this.config?.affinity?.[family]?.[candidateHandle]) || 0)
          .filter((score) => score > 0)
          .sort((a, b) => b - a);

        if (!scores.length) return 0;

        const supportingScore = scores.slice(1).reduce((total, score) => total + score * 0.15, 0);
        return Math.min(100, scores[0] + supportingScore);
      }

      getBundleMatchCount(bundleRule, cartItems) {
        if (!bundleRule?.members?.length) return 0;

        return bundleRule.members.reduce((count, member) => {
          const memberToken = this.normalizeToken(member.variantToken);
          const matched = cartItems.some((item) => {
            if (item.handle !== member.handle) return false;
            if (!memberToken) return true;
            return this.normalizeToken(item.variantTitle).includes(memberToken);
          });
          return count + Number(matched);
        }, 0);
      }

      getPriceTier(priceCents) {
        if (priceCents <= 790) return 'small';
        if (priceCents <= 1290) return 'medium';
        return 'large';
      }

      getGapFit(priceCents, remainingCents) {
        if (remainingCents <= 0 || priceCents <= 0) return 0;

        if (priceCents >= remainingCents) {
          const overshoot = priceCents - remainingCents;
          return Math.max(0, 100 - (overshoot / Math.max(remainingCents, 500)) * 70);
        }

        return Math.max(0, 48 * (priceCents / remainingCents));
      }

      getPriceEase(priceCents) {
        if (priceCents <= 690) return 100;
        if (priceCents <= 790) return 92;
        if (priceCents <= 890) return 84;
        if (priceCents <= 1090) return 72;
        if (priceCents <= 1290) return 60;
        if (priceCents <= 1590) return 44;
        if (priceCents <= 1990) return 28;
        return 12;
      }

      buildCandidate(element, cartItems, cartFamilies, totalCents, remainingCents) {
        const handle = element.dataset.productHandle || '';
        const productId = Number(element.dataset.productId) || 0;
        const variantId = Number(element.dataset.variantId) || 0;
        const variantTitle = element.dataset.variantTitle || '';
        const priceCents = Number(element.dataset.priceCents) || 0;
        const compareAtPriceCents = Number(element.dataset.compareAtPriceCents) || 0;
        const available = element.dataset.available === 'true';
        const productConfig = this.config?.products?.[handle] || {};
        const bundleRule = this.config?.bundles?.[handle];
        const exactVariantInCart = cartItems.some((item) => item.variantId === variantId);
        const productInCart = cartItems.some(
          (item) => item.handle === handle || (productId && item.productId === productId)
        );

        if (!available || !variantId || priceCents <= 0 || exactVariantInCart || productInCart) {
          return null;
        }

        const bundleMatchCount = this.getBundleMatchCount(bundleRule, cartItems);
        let affinityScore = this.getAffinityScore(handle, cartFamilies);

        if (bundleMatchCount > 0) {
          affinityScore = Math.max(
            affinityScore,
            Math.min(100, (Number(bundleRule.baseScore) || 80) + (bundleMatchCount - 1) * 8)
          );
        }

        const isPromo = compareAtPriceCents > priceCents;
        const isBundleOpportunity = Boolean(bundleRule && bundleMatchCount > 0);
        const relevant = affinityScore >= 30 || isBundleOpportunity;
        if (!relevant) return null;

        const regime = totalCents < 3000 ? 'low' : remainingCents <= 1500 ? 'near' : 'mid';
        const gapFit = this.getGapFit(priceCents, remainingCents);
        const priceEase = this.getPriceEase(priceCents);
        const priority = Math.max(0, Number(productConfig.priority) || 0);
        const preferredToken = this.normalizeToken(productConfig.preferredVariantToken);
        const preferredVariant = Boolean(
          preferredToken && this.normalizeToken(variantTitle).includes(preferredToken)
        );
        const reviewUpgrade = Boolean(bundleRule?.reviewOnMemberMatch && bundleMatchCount > 0);
        const directScore = reviewUpgrade ? 0 : 6;
        const promoScore = isPromo ? 4 : 0;
        let rankingScore;

        if (regime === 'near') {
          rankingScore =
            affinityScore * 0.58 +
            gapFit * 1.05 +
            directScore +
            priority * 2 +
            promoScore +
            Number(preferredVariant) * 2;
        } else if (regime === 'mid') {
          rankingScore =
            affinityScore * 0.82 +
            gapFit * 0.35 +
            directScore +
            priority * 2 +
            promoScore +
            Number(preferredVariant) * 6;
        } else {
          rankingScore =
            affinityScore +
            priceEase * 0.22 +
            directScore +
            priority * 2 +
            promoScore +
            Number(preferredVariant) * 8;
        }

        return {
          element,
          handle,
          productId,
          variantId,
          variantTitle,
          priceCents,
          compareAtPriceCents,
          priceTier: this.getPriceTier(priceCents),
          affinityScore,
          gapFit,
          rankingScore,
          opportunityScore:
            affinityScore * 0.55 +
            Number(isPromo) * 30 +
            Number(isBundleOpportunity) * 25 +
            bundleMatchCount * 8 +
            priority * 2,
          isPromo,
          isBundleOpportunity,
          reviewUpgrade,
          closesThreshold: remainingCents > 0 && priceCents >= remainingCents,
          overshootCents:
            remainingCents > 0 && priceCents >= remainingCents
              ? priceCents - remainingCents
              : Number.POSITIVE_INFINITY,
          regime,
        };
      }

      selectSuggestions(candidates, regime) {
        if (!candidates.length) return [];

        const bestByProduct = new Map();
        [...candidates]
          .sort((a, b) => b.rankingScore - a.rankingScore)
          .forEach((candidate) => {
            if (!bestByProduct.has(candidate.handle)) bestByProduct.set(candidate.handle, candidate);
          });

        const pool = [...bestByProduct.values()];
        const chosen = [];
        const take = (candidate) => {
          if (!candidate || chosen.some((item) => item.handle === candidate.handle)) return;
          chosen.push(candidate);
        };
        const remaining = () => pool.filter((candidate) => !chosen.includes(candidate));

        if (regime === 'near') {
          const preciseCloser = pool
            .filter((candidate) => candidate.closesThreshold)
            .sort(
              (a, b) =>
                a.overshootCents - b.overshootCents ||
                b.affinityScore - a.affinityScore ||
                b.rankingScore - a.rankingScore
            )[0];
          take(preciseCloser || [...pool].sort((a, b) => b.rankingScore - a.rankingScore)[0]);
          take(
            remaining().sort(
              (a, b) => b.affinityScore + b.rankingScore * 0.2 - (a.affinityScore + a.rankingScore * 0.2)
            )[0]
          );
        } else {
          take(
            [...pool].sort(
              (a, b) => b.affinityScore + b.rankingScore * 0.15 - (a.affinityScore + a.rankingScore * 0.15)
            )[0]
          );

          const firstTier = chosen[0]?.priceTier;
          take(
            remaining().sort((a, b) => {
              const aScore = a.rankingScore + Number(a.priceTier !== firstTier) * (regime === 'low' ? 12 : 5);
              const bScore = b.rankingScore + Number(b.priceTier !== firstTier) * (regime === 'low' ? 12 : 5);
              return bScore - aScore;
            })[0]
          );
        }

        const opportunity = remaining()
          .filter((candidate) => candidate.isPromo || candidate.isBundleOpportunity)
          .sort((a, b) => b.opportunityScore - a.opportunityScore)[0];
        take(opportunity);

        while (chosen.length < 3) {
          const usedTiers = new Set(chosen.map((candidate) => candidate.priceTier));
          const next = remaining().sort((a, b) => {
            const aScore = a.rankingScore + Number(!usedTiers.has(a.priceTier)) * (regime === 'low' ? 8 : 3);
            const bScore = b.rankingScore + Number(!usedTiers.has(b.priceTier)) * (regime === 'low' ? 8 : 3);
            return bScore - aScore;
          })[0];
          if (!next) break;
          take(next);
        }

        return chosen.slice(0, 3);
      }

      updateSuggestions(itemCount, totalCents, remainingCents, cartItems = []) {
        if (!this.suggestionsElement) return;

        const thresholdReached = totalCents >= this.thresholdCents;
        if (this.precheckoutSuccessElement) {
          this.precheckoutSuccessElement.hidden = !thresholdReached;
        }

        this.candidateElements.forEach((candidate) => {
          candidate.hidden = true;
          const directButton = candidate.querySelector('[data-moretti-add]');
          const upgradeLink = candidate.querySelector('[data-moretti-upgrade-link]');
          if (directButton) {
            directButton.hidden = false;
            directButton.style.display = '';
          }
          if (upgradeLink) {
            upgradeLink.hidden = true;
            upgradeLink.style.display = 'none';
          }
        });

        if (this.context !== 'precheckout' || itemCount === 0 || thresholdReached) {
          this.suggestionsElement.hidden = true;
          return;
        }

        const normalizedCartItems = this.engine.normalizeItems(cartItems);
        const regime = totalCents < 3000 ? 'low' : remainingCents <= 1500 ? 'near' : 'mid';
        const candidates = this.candidateElements
          .map((element) =>
            this.engine.buildCandidate(element, {
              cartItems: normalizedCartItems,
              sourceItems: normalizedCartItems,
              totalCents,
              remainingCents,
            })
          )
          .filter(Boolean);
        const selectedCandidates = this.engine.selectSuggestions(candidates, regime);
        selectedCandidates.forEach((candidate) => {
          if (candidate.reviewUpgrade) {
            const directButton = candidate.element.querySelector('[data-moretti-add]');
            const upgradeLink = candidate.element.querySelector('[data-moretti-upgrade-link]');
            if (directButton) {
              directButton.hidden = true;
              directButton.style.display = 'none';
            }
            if (upgradeLink) {
              upgradeLink.hidden = false;
              upgradeLink.style.display = '';
            }
          }
          candidate.element.hidden = false;
          this.suggestionList?.append(candidate.element);
        });
        this.suggestionsElement.hidden = selectedCandidates.length === 0;
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
        }
      }
    }
  );
}

if (!customElements.get('moretti-related-products')) {
  customElements.define(
    'moretti-related-products',
    class MorettiRelatedProducts extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.list = this.querySelector('[data-moretti-related-list]');
        this.candidateElements = Array.from(
          this.querySelectorAll('[data-moretti-related-candidate]')
        );
        this.config = this.readEmbeddedJson('[data-moretti-merchandising-config]', {
          products: {},
          functionAffinity: {},
          productAffinity: {},
          bundles: {},
        });
        this.engine = new MorettiSuggestionEngine(this.config);
        this.sourceItems = this.engine.normalizeItems(
          this.readEmbeddedJson('[data-moretti-related-source]', [])
        );
        this.cartItems = this.engine.normalizeItems(
          this.readEmbeddedJson('[data-moretti-related-cart]', [])
        );
        this.thresholdCents = Number(this.dataset.thresholdCents) || 4990;
        this.totalCents = Math.max(0, Number(this.dataset.cartTotalCents) || 0);
        // Le raccomandazioni si trovano dopo la scheda prodotto:
        // evitiamo tre richieste HTTP prima che il visitatore si avvicini alla sezione.
        this.subscribeToCartUpdates();
        this.observeRecommendationSection();
      }

      observeRecommendationSection() {
        // Ancora visibile: il componente e' nascosto fino al caricamento e non ha altezza.
        const target = this.parentElement?.querySelector('[data-moretti-related-anchor]') ||
          this.closest('.shopify-section') || this.parentElement;
        if (!target || !('IntersectionObserver' in window)) {
          this.relatedActivated = true;
          this.render();
          return;
        }
        this.relatedObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.relatedObserver?.disconnect();
          this.relatedObserver = null;
          this.relatedActivated = true;
          this.render();
        }, { rootMargin: '300px 0px' });
        this.relatedObserver.observe(target);
      }

      disconnectedCallback() {
        this.relatedObserver?.disconnect();
        this.relatedObserver = null;
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
      }

      readEmbeddedJson(selector, fallback) {
        const source = this.querySelector(selector);
        if (!source?.textContent) return fallback;
        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          return fallback;
        }
      }

      subscribeToCartUpdates() {
        if (
          this.cartUpdateUnsubscriber ||
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          if (Array.isArray(cartData?.items)) {
            this.cartItems = this.engine.normalizeItems(cartData.items);
            this.totalCents = Math.max(0, Number(cartData.total_price) || 0);
            this.render();
            return Promise.resolve();
          }
          return this.refreshFromCart();
        });
      }

      refreshFromCart() {
        if (this.refreshPromise) return this.refreshPromise;
        this.refreshPromise = fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Cart unavailable');
            return response.json();
          })
          .then((cart) => {
            this.cartItems = this.engine.normalizeItems(cart.items);
            this.totalCents = Math.max(0, Number(cart.total_price) || 0);
            this.render();
          })
          .catch(() => undefined)
          .finally(() => {
            this.refreshPromise = undefined;
          });
        return this.refreshPromise;
      }

      // Memorizza l'HTML per handle; riutilizziamo il risultato senza rifare richieste.
      getRecommendationCard(candidate) {
        const handle = candidate.handle;
        const url = candidate.element.dataset.productUrl;
        if (!handle || !url) return Promise.resolve(null);
        this.cardRequests = this.cardRequests || new Map();
        if (!this.cardRequests.has(handle)) {
          const request = (async () => {
            const address = new URL(url, window.location.origin);
            address.searchParams.set('section_id', 'moretti-related-card');
            const response = await fetch(address, {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' },
            });
            if (!response.ok) throw new Error('Prodotto correlato non disponibile');
            const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = documentFragment.querySelector('[data-moretti-related-rendered]');
            if (!card || card.dataset.morettiRelatedHandle !== handle) {
              throw new Error('Risposta prodotto correlato non valida');
            }
            return card.outerHTML;
          })().catch(() => {
            this.cardRequests.delete(handle);
            return null;
          });
          this.cardRequests.set(handle, request);
        }
        return this.cardRequests.get(handle).then((markup) => {
          if (!markup) return null;
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          return doc.querySelector('[data-moretti-related-rendered]');
        });
      }

      render() {
        if (!this.relatedActivated) return;
        const generation = this.renderGeneration = (this.renderGeneration || 0) + 1;
        if (!this.list || !this.sourceItems.length || this.totalCents >= this.thresholdCents) {
          this.list?.replaceChildren();
          this.hidden = true;
          return;
        }

        const remainingCents = Math.max(0, this.thresholdCents - this.totalCents);
        const sourceItems = [...this.sourceItems, ...this.cartItems];
        const regime = this.totalCents < 3000 ? 'low' : remainingCents <= 1500 ? 'near' : 'mid';
        const candidates = this.candidateElements
          .map((element) =>
            this.engine.buildCandidate(element, {
              cartItems: this.cartItems,
              sourceItems,
              totalCents: this.totalCents,
              remainingCents,
            })
          )
          .filter(Boolean);
        const selected = this.engine.selectSuggestions(candidates, regime);
        this.hidden = true;
        if (!selected.length) {
          this.list.replaceChildren();
          return;
        }

        Promise.all(selected.map((candidate) => this.getRecommendationCard(candidate)))
          .then((cards) => {
            if (!this.isConnected || this.renderGeneration !== generation) return;
            const usableCards = cards.filter(Boolean);
            this.list.replaceChildren(...usableCards);
            this.hidden = usableCards.length === 0;
          })
          .catch(() => {
            if (this.renderGeneration === generation) this.hidden = true;
          });
      }
    }
  );
}

window.morettiFastCartState = window.morettiFastCartState || {
  bypassCheckout: false,
  checkoutSourceId: '',
  reopenPrecheckout: false,
  returnFocus: null,
  suppressDrawerUntil: 0,
};

const updateMorettiPrecheckoutFromSection = (sectionHtml) => {
  const currentPanel = document.querySelector('[data-moretti-precheckout]');
  if (!currentPanel || !sectionHtml) return false;

  const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const sourcePanel = parsedSection.querySelector('[data-moretti-precheckout]');
  if (!sourcePanel) return false;

  const currentGoal = currentPanel.querySelector('moretti-cart-goal');
  const sourceGoal = sourcePanel.querySelector('moretti-cart-goal');
  // Mentre il pannello e' aperto, conserva catalogo, bottoni e selezione corrente.
  // Il cartUpdate sincronizzera' il goal con i dati del carrello aggiornato.
  const keepCurrentGoal =
    !currentPanel.hidden && currentGoal?.dataset.morettiCatalogReady === 'true';
  if (currentGoal && sourceGoal && !keepCurrentGoal) currentGoal.replaceWith(sourceGoal);

  const currentTotal = currentPanel.querySelector('.moretti-precheckout__summary strong');
  const sourceTotal = sourcePanel.querySelector('.moretti-precheckout__summary strong');
  if (currentTotal && sourceTotal) currentTotal.textContent = sourceTotal.textContent;

  const currentCheckout = currentPanel.querySelector('[data-moretti-precheckout-checkout]');
  const sourceCheckout = sourcePanel.querySelector('[data-moretti-precheckout-checkout]');
  if (currentCheckout && sourceCheckout) currentCheckout.textContent = sourceCheckout.textContent;

  return true;
};

if (!window.morettiCartGoalAddInitialized) {
  window.morettiCartGoalAddInitialized = true;
  window.morettiCartGoalAddInFlight = false;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-moretti-add]');
    if (!button || button.disabled || window.morettiCartGoalAddInFlight) return;

    const goal = button.closest('moretti-cart-goal');
    const errorElement = goal?.querySelector('[data-moretti-goal-error]');
    const variantId = Number(button.dataset.variantId);
    const quantity = Math.max(1, Number(button.dataset.addQuantity) || 1);
    const addedFromPrecheckout = Boolean(button.closest('[data-moretti-precheckout]'));
    if (!variantId) return;

    window.morettiCartGoalAddInFlight = true;
    const goalButtons = Array.from(goal?.querySelectorAll('[data-moretti-add]') || []);
    goalButtons.forEach((goalButton) => {
      goalButton.disabled = true;
    });
    button.setAttribute('aria-busy', 'true');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    try {
      const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
      const mainCartFooter = document.getElementById('main-cart-footer');
      const mainCartFooterSectionId = mainCartFooter?.dataset.id;
      if (mainCartFooterSectionId) sectionIds.push(mainCartFooterSectionId);

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sectionIds,
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      const responseText = await response.text();
      let result;

      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Risposta Shopify non valida. Riprova.');
      }

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Impossibile aggiungere il prodotto');
      }

      const fetchAuthoritativeCart = async () => {
        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        const separator = cartUrl.includes('?') ? '&' : '?';
        const cartResponse = await fetch(
          `${cartUrl}${separator}_moretti_add_verify=${Date.now()}`,
          {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          }
        );
        if (!cartResponse.ok) throw new Error('Carrello Shopify non disponibile. Riprova.');
        return cartResponse.json();
      };

      let cart = await fetchAuthoritativeCart();
      const hasAddedVariant = () =>
        Array.isArray(cart?.items) &&
        cart.items.some(
          (item) => Number(item.variant_id || item.id) === variantId && Number(item.quantity) > 0
        );

      if (!hasAddedVariant()) cart = await fetchAuthoritativeCart();
      if (!hasAddedVariant()) {
        throw new Error('L’aggiunta non è stata confermata da Shopify. Riprova.');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.renderContents && result.sections?.['cart-drawer']) {
        if (addedFromPrecheckout) {
          updateMorettiPrecheckoutFromSection(result.sections['cart-drawer']);
        } else {
          cartDrawer.renderContents(result);
        }
      }

      if (mainCartFooterSectionId && result.sections?.[mainCartFooterSectionId]) {
        const parsedSection = new DOMParser().parseFromString(
          result.sections[mainCartFooterSectionId],
          'text/html'
        );
        const sourceContents = parsedSection.querySelector('#main-cart-footer .js-contents');
        const targetContents = document.querySelector('#main-cart-footer .js-contents');
        if (sourceContents && targetContents) targetContents.replaceWith(sourceContents);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'moretti-cart-goal',
          productVariantId: variantId,
          cartData: cart,
        });
      }

    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.message || 'Non è stato possibile aggiungere il prodotto.';
        errorElement.hidden = false;
      }
    } finally {
      window.morettiCartGoalAddInFlight = false;
      goalButtons.forEach((goalButton) => {
        if (goalButton.isConnected) goalButton.disabled = false;
      });
      if (button.isConnected) button.removeAttribute('aria-busy');
    }
  });
}

if (!window.morettiFastCartInitialized) {
  window.morettiFastCartInitialized = true;

  const state = window.morettiFastCartState;

  const formatMoney = (cents, locale = 'it-IT', currency = 'EUR') => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const getPanel = () => document.querySelector('[data-moretti-precheckout]');

  const updateHeaderCart = (cart) => {
    const headerCart = document.getElementById('cart-icon-bubble');
    if (!headerCart) return;

    const itemCount = Math.max(0, Number(cart?.item_count) || 0);
    headerCart.classList.add('moretti-header-cart');

    let countElement = headerCart.querySelector(
      '.moretti-header-cart__count, .cart-count-bubble > span[aria-hidden="true"]'
    );
    let legacyBubble = headerCart.querySelector('.cart-count-bubble');

    if (!countElement && !headerCart.querySelector('.moretti-header-cart__count')) {
      legacyBubble = document.createElement('div');
      legacyBubble.className = 'cart-count-bubble';
      legacyBubble.innerHTML =
        '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      headerCart.appendChild(legacyBubble);
      countElement = legacyBubble.querySelector('span[aria-hidden="true"]');
    }

    if (countElement) countElement.textContent = itemCount > 99 ? '99+' : String(itemCount);
    headerCart.querySelectorAll('.visually-hidden').forEach((accessibleCount) => {
      accessibleCount.textContent = `${itemCount} ${itemCount === 1 ? 'articolo' : 'articoli'}`;
    });

    headerCart.setAttribute(
      'aria-label',
      `Carrello: ${itemCount} ${itemCount === 1 ? 'prodotto' : 'prodotti'}`
    );
  };

  const updateFastCart = (cart) => {
    updateHeaderCart(cart);

    const itemCount = Math.max(0, Number(cart?.item_count) || 0);
    document.body.classList.toggle('moretti-cart-has-items', itemCount > 0);

    const fastCart = document.querySelector('[data-moretti-fast-cart]');
    if (!fastCart) return;

    const totalCents = Math.max(0, Number(cart?.total_price) || 0);
    const locale = fastCart.dataset.locale || 'it-IT';
    const currency = cart?.currency || fastCart.dataset.currency || 'EUR';
    const countElement = fastCart.querySelector('[data-moretti-fast-cart-count]');
    const totalElement = fastCart.querySelector('[data-moretti-fast-cart-total]');
    const shouldHide = itemCount === 0 || window.location.pathname.replace(/\/+$/, '') === '/cart';

    if (countElement) countElement.textContent = String(itemCount);
    if (totalElement) totalElement.textContent = formatMoney(totalCents, locale, currency);
    fastCart.setAttribute(
      'aria-label',
      `Apri il carrello: ${itemCount} ${itemCount === 1 ? 'articolo' : 'articoli'}, totale ${formatMoney(
        totalCents,
        locale,
        currency
      )}`
    );
    fastCart.hidden = shouldHide;
    document.body.classList.toggle('moretti-has-fast-cart', !shouldHide);
  };

  const refreshFastCart = () =>
    fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then((cart) => {
        updateFastCart(cart);
        return cart;
      })
      .catch(() => undefined);

  const closePrecheckout = ({ restoreFocus = true } = {}) => {
    const panel = getPanel();
    if (!panel || panel.hidden) return;

    panel.classList.remove('is-active');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('moretti-precheckout-open', 'overflow-hidden');

    const focusTarget = state.returnFocus;
    setTimeout(() => {
      if (panel.isConnected) panel.hidden = true;
      if (restoreFocus && focusTarget?.isConnected) focusTarget.focus();
    }, 220);
  };

  const findCheckoutSubmitter = () => {
    if (state.checkoutSourceId) {
      const source = document.getElementById(state.checkoutSourceId);
      if (source) return source;
    }
    return document.querySelector('#CartDrawer-Checkout, #checkout, button[name="checkout"]');
  };

  const proceedToCheckout = () => {
    const submitter = findCheckoutSubmitter();
    state.bypassCheckout = true;
    closePrecheckout({ restoreFocus: false });

    setTimeout(() => {
      if (submitter?.form && submitter.isConnected) {
        submitter.click();
        return;
      }
      window.location.assign('/checkout');
    });
  };

  // Shopify Section Rendering API: il catalogo varianti viene preparato solo
  // quando il cliente avvia il checkout, non su ogni pagina del negozio.
  const ensurePrecheckoutCatalog = async (panel) => {
    const goal = panel.querySelector('moretti-cart-goal[data-context="precheckout"]');
    if (!goal) return false;

    if (goal.morettiCatalogPromise) return goal.morettiCatalogPromise;

    goal.morettiCatalogPromise = (async () => {
      // Timeout complessivo: i suggerimenti non devono bloccare il checkout.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      try {
      if (goal.dataset.morettiCatalogReady !== 'true') {
        const cartUrl = window.routes?.cart_url || '/cart';
        const separator = cartUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${cartUrl}${separator}section_id=moretti-precheckout-feed`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return false;
        const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = markup.querySelector('[data-moretti-precheckout-feed]');
        const list = goal.querySelector('[data-moretti-suggestion-list]');
        if (!feed || !list) return false;

        list.replaceChildren(...Array.from(feed.childNodes, (node) => document.importNode(node, true)));
        goal.suggestionList = list;
        goal.candidateElements = Array.from(goal.querySelectorAll('[data-moretti-candidate]'));
        goal.config = goal.readEmbeddedJson('[data-moretti-merchandising-config]', {
          products: {}, functionAffinity: {}, productAffinity: {}, bundles: {},
        });
        goal.engine = new MorettiSuggestionEngine(goal.config);
        goal.dataset.morettiCatalogReady = 'true';
      }

      // Il carrello viene letto nuovamente: prezzi e disponibilità sono quelli attuali.
      const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!cartResponse.ok) return false;
      const cart = await cartResponse.json();
      if (!goal.isConnected) return false;
      goal.render(cart);
      return true;
      } finally {
        clearTimeout(timeoutId);
      }
    })().catch(() => false).finally(() => {
      goal.morettiCatalogPromise = null;
    });

    return goal.morettiCatalogPromise;
  };

  const openPrecheckout = async ({ submitter, preserveFocus = false } = {}) => {
    if (state.precheckoutOpening) return;
    state.precheckoutOpening = true;

    if (submitter) {
      state.checkoutSourceId = submitter.id || '';
      state.returnFocus = submitter;
    } else if (!preserveFocus) {
      state.returnFocus = document.activeElement;
    }

    try {
      const originalPanel = getPanel();
      if (!originalPanel || !(await ensurePrecheckoutCatalog(originalPanel))) {
        proceedToCheckout();
        return;
      }

      const panel = getPanel();
      // Il carrello potrebbe essere stato aggiornato mentre caricavamo il catalogo.
      if (!panel || panel !== originalPanel) {
        proceedToCheckout();
        return;
      }

      const visibleCandidates = Array.from(panel.querySelectorAll('[data-moretti-candidate]')).filter(
        (candidate) => !candidate.hidden
      );
      if (visibleCandidates.length === 0) {
        proceedToCheckout();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.classList.contains('active')) cartDrawer.close();

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('moretti-precheckout-open', 'overflow-hidden');
      requestAnimationFrame(() => {
        panel.classList.add('is-active');
        panel.querySelector('[data-moretti-precheckout-close]')?.focus();
      });
    } finally {
      state.precheckoutOpening = false;
    }
  };

  window.morettiFastCart = {
    closePrecheckout,
    openPrecheckout,
    proceedToCheckout,
    refreshFastCart,
    updateFastCart,
    updateHeaderCart,
  };

  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawerElement = customElements.get('cart-drawer');
    if (!CartDrawerElement || CartDrawerElement.prototype.morettiFastCartPatched) return;

    const originalOpen = CartDrawerElement.prototype.open;
    CartDrawerElement.prototype.open = function morettiFastOpen(triggeredBy) {
      if (!triggeredBy && Date.now() < state.suppressDrawerUntil) {
        state.suppressDrawerUntil = 0;
        return;
      }
      return originalOpen.call(this, triggeredBy);
    };
    CartDrawerElement.prototype.morettiFastCartPatched = true;
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target.closest('product-form')) {
        state.suppressDrawerUntil = Date.now() + 5000;
        return;
      }

      const submitter = event.submitter;
      if (submitter?.name !== 'checkout') return;
      if (state.bypassCheckout) {
        state.bypassCheckout = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      openPrecheckout({ submitter });
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"]');
      if (checkoutButton) {
        if (state.bypassCheckout) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        openPrecheckout({ submitter: checkoutButton });
        return;
      }

      const fastCart = event.target.closest('[data-moretti-fast-cart]');
      if (fastCart) {
        document.querySelector('cart-drawer')?.open(fastCart);
        return;
      }

      if (event.target.closest('[data-moretti-precheckout-checkout]')) {
        proceedToCheckout();
        return;
      }

      if (
        event.target.closest('[data-moretti-precheckout-close]') ||
        event.target.closest('[data-moretti-precheckout-continue]') ||
        event.target.closest('[data-moretti-precheckout-overlay]')
      ) {
        closePrecheckout();
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && getPanel()?.classList.contains('is-active')) {
      event.preventDefault();
      closePrecheckout();
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
      if (cartData?.item_count !== undefined && cartData?.total_price !== undefined) {
        updateFastCart(cartData);
        return Promise.resolve();
      }
      return refreshFastCart();
    });
  }

  refreshFastCart();

}
